/**
 * touchSelector.ts — Manual Sequence Touch Selector helpers (pure, no React).
 *
 * Mirrors the backend readiness states in
 * outrena-backend/app/features/sequences/touch_readiness.py and the
 * GET /api/v1/sequences/touch-eligibility + POST /api/v1/sequences/template-send
 * response shapes. Kept free of React / path aliases so it can be unit-tested
 * directly (tests/touch-selector.test.ts).
 */

export type TouchReadinessState =
  | 'ready'
  | 'waiting'
  | 'stopped'
  | 'needs_resend'
  | 'not_started'
  | 'already_has'
  | 'ai_managed';

export interface TouchEligibilityTouch {
  touchNumber: number;
  sendDay: number;
  angle: string;
  gapDays: number;
  counts: Partial<Record<TouchReadinessState, number>>;
  nextDueAt?: string | null;
}

export interface TouchEligibilityResponse {
  campaignId: string;
  icpProfileId: string;
  totalProspects: number;
  enforceCadenceGap: boolean;
  recommendedTouch: number | null;
  touches: TouchEligibilityTouch[];
}

export interface TemplateSendSkip {
  prospectId: string;
  prospectName: string;
  state: TouchReadinessState;
  reason: string;
  dueAt?: string | null;
}

export interface SkipGroup {
  state: TouchReadinessState;
  label: string;
  hint: string;
  count: number;
  names: string[];
  more: number;
  reasons: string[];
}

/** Display order of states (matches the backend STATE_ORDER). */
export const STATE_ORDER: TouchReadinessState[] = [
  'ready',
  'waiting',
  'stopped',
  'needs_resend',
  'not_started',
  'already_has',
  'ai_managed',
];

export const STATE_LABELS: Record<TouchReadinessState, string> = {
  ready: 'ready',
  waiting: 'waiting',
  stopped: 'stopped',
  needs_resend: 'need resend',
  not_started: 'not started',
  already_has: 'already have it',
  ai_managed: 'AI-managed',
};

/** What the rep should do for each state (design doc §4.4). */
export const STATE_HINTS: Record<TouchReadinessState, string> = {
  ready: 'Render this touch',
  waiting: 'Previous touch not sent yet, or cadence gap not reached — come back later',
  stopped: 'Replied, bounced, blocked or unsubscribed — sequence has ended',
  needs_resend: 'Previous touch failed — fix and resend it first',
  not_started: 'Has not received the previous touch — appears under an earlier touch',
  already_has: 'This touch is already scheduled or sent — left untouched',
  ai_managed: 'Sequence was generated by AI — manage it from the AI tab',
};

export const STATE_COLORS: Record<TouchReadinessState, string> = {
  ready: 'text-green-700',
  waiting: 'text-amber-700',
  stopped: 'text-red-700',
  needs_resend: 'text-amber-700',
  not_started: 'text-muted-foreground',
  already_has: 'text-muted-foreground',
  ai_managed: 'text-emerald-700',
};

/** "NewEvidence" → "New Evidence". */
export function angleLabel(angle: string): string {
  return angle.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

/** ISO date → "17 Oct" (UTC, so it matches the backend due date). */
export function formatDueDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

export function readyCount(touch: TouchEligibilityTouch | undefined | null): number {
  return touch?.counts?.ready ?? 0;
}

/** A touch can be chosen only when at least one prospect is ready for it. */
export function isTouchSelectable(touch: TouchEligibilityTouch | undefined | null): boolean {
  return readyCount(touch) > 0;
}

/**
 * Dropdown label, e.g. "Touch 2 · Day 4 — 41 ready · 4 waiting · 2 stopped".
 * Zero counts are omitted, except "ready" which is always shown.
 */
export function formatTouchOption(touch: TouchEligibilityTouch): string {
  const parts: string[] = [`${readyCount(touch)} ready`];
  for (const state of STATE_ORDER) {
    if (state === 'ready') continue;
    const n = touch.counts?.[state] ?? 0;
    if (n > 0) parts.push(`${n} ${STATE_LABELS[state]}`);
  }
  return `Touch ${touch.touchNumber} · Day ${touch.sendDay} — ${parts.join(' · ')}`;
}

/** One-line explanation shown under the selector for the chosen touch. */
export function describeTouch(touch: TouchEligibilityTouch, enforceGap: boolean): string {
  const angle = angleLabel(touch.angle);
  const timing =
    touch.touchNumber === 1
      ? 'first email of the sequence'
      : enforceGap && touch.gapDays > 0
        ? `sent ${touch.gapDays} day${touch.gapDays === 1 ? '' : 's'} after Touch ${touch.touchNumber - 1}`
        : `sent after Touch ${touch.touchNumber - 1}`;
  const due = touch.nextDueAt ? ` Next prospect becomes ready on ${formatDueDate(touch.nextDueAt)}.` : '';
  return `${angle} — ${timing}.${due}`;
}

/**
 * Touch to pre-select: keep the current one if it is still selectable,
 * otherwise the backend's recommendation (lowest touch with ready prospects).
 */
export function pickTouch(
  data: TouchEligibilityResponse | undefined | null,
  current: number | null,
): number | null {
  if (!data) return current;
  if (current !== null) {
    const cur = data.touches.find((t) => t.touchNumber === current);
    if (isTouchSelectable(cur)) return current;
  }
  return data.recommendedTouch ?? null;
}

/** Group template-send skips by state for the summary panel. */
export function groupSkips(skipped: TemplateSendSkip[], maxNames = 5): SkipGroup[] {
  const byState = new Map<TouchReadinessState, TemplateSendSkip[]>();
  for (const s of skipped) {
    const list = byState.get(s.state) ?? [];
    list.push(s);
    byState.set(s.state, list);
  }
  const groups: SkipGroup[] = [];
  for (const state of STATE_ORDER) {
    const list = byState.get(state);
    if (!list || list.length === 0) continue;
    const reasons = Array.from(new Set(list.map((s) => s.reason))).slice(0, 3);
    groups.push({
      state,
      label: STATE_LABELS[state],
      hint: STATE_HINTS[state],
      count: list.length,
      names: list.slice(0, maxNames).map((s) => s.prospectName),
      more: Math.max(0, list.length - maxNames),
      reasons,
    });
  }
  return groups;
}
